import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React from "react";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import ToolbarGroup from "../../components/toolbar/ToolbarGroup";
import ToolbarIconButton from "../../components/toolbar/ToolbarIconButton";
import ToolbarSpacer from "../../components/toolbar/ToolbarSpacer";
import ToolbarTextSlugButton from "../../components/toolbar/ToolbarTextSlugButton";
import ToolbarView from "../../components/toolbar/ToolbarView";
import {
  COLLAPSED_INSPECTOR_PANEL,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW,
  TUTORIAL_PANEL
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import PubSubTopics from "../../constants/pubSubTopics";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { autoUpdatesOperations, autoUpdatesSelectors } from "../../modules/autoUpdates";
import { definitionSelectors } from "../../modules/definition";
import { mindmapOperations, mindmapSelectors } from "../../modules/mindmaps";
import { narrativeSelectors } from "../../modules/narrative";
import { preferencesSelectors } from "../../modules/preferences";
import { registrationSelectors } from "../../modules/registration";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { tutorialDisplaySelectors } from "../../tutorial/selectors";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps, wrapEditingComponent } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import ActiveViewButtonBar from "./ActiveViewButtonBar";
import ActiveViewSelector from "./ActiveViewSelector";

const TEXT_BUTTON_BAR_MIN_WIDTH = 865;
const TEXT_BUTTON_BAR_MIN_WIDTH_NO_NARRATIVE = 655;
const ICON_BUTTON_BAR_MIN_WIDTH = 350;

const CREATE_MINDMAP_ID = "create_new_mindmap";

const onRegistrationClick = () => {
  PubSub.publish(PubSubTopics.sendNativeMessage, {
    msg: nativeCommsBroadcastMessageTypes.SHOW_REGISTRATION_MODAL
  });
};

const Toolbar = props => {
  const isUpdateAvailable =
    props.autoUpdatesData.isEditingUpdateAvailable || props.autoUpdatesData.isReadonlyUpdateAvailable;
  const isReleaseNotesNew = props.autoUpdatesData.availableReleaseNotes && props.autoUpdatesData.isReleaseNotesNew;
  const isReadonly = preprocessorQuery.isReadonly();

  const SCALED_TEXT_BUTTON_BAR_MIN_WIDTH = scalePixelsToFontSize(TEXT_BUTTON_BAR_MIN_WIDTH, props.globalFontSize);
  const SCALED_TEXT_BUTTON_BAR_MIN_WIDTH_NO_NARRATIVE = scalePixelsToFontSize(
    TEXT_BUTTON_BAR_MIN_WIDTH_NO_NARRATIVE,
    props.globalFontSize
  );
  const minButtonTextWidth = props.includeNarrative
    ? SCALED_TEXT_BUTTON_BAR_MIN_WIDTH
    : SCALED_TEXT_BUTTON_BAR_MIN_WIDTH_NO_NARRATIVE;

  return (
    <ToolbarView domId={props.domId} className="headerToolbar">
      <ToolbarGroup position="leftTop" flex={2}>
        {props.containerRect.width >= ICON_BUTTON_BAR_MIN_WIDTH ? (
          <ActiveViewButtonBar
            {...props}
            disabled={props.isWelcomeVisible}
            iconOnly={props.containerRect.width < minButtonTextWidth}
          />
        ) : (
          <ActiveViewSelector {...props} disabled={props.isWelcomeVisible} />
        )}
      </ToolbarGroup>
      <ToolbarGroup position="right">
        {isUpdateAvailable ? (
          <ToolbarTextSlugButton text="UPDATE" color="statusValid" onClick={props.onUpdateClick} />
        ) : isReleaseNotesNew ? (
          <ToolbarTextSlugButton text="NEW" color="statusValid" onClick={null} />
        ) : isReadonly || props.isTrialStarted ? (
          <ToolbarTextSlugButton
            text={isReadonly ? "READ-ONLY" : "TRIAL"}
            color="statusWarn"
            onClick={onRegistrationClick}
          />
        ) : props.updatesHaveExpired ? (
          <ToolbarTextSlugButton text="RENEW" color="iconMono" onClick={onRegistrationClick} />
        ) : null}
        <ToolbarIconButton
          icon="sidebarSettings"
          tooltip={props.isWelcomeVisible ? null : "Settings"}
          active={false}
          disabled={props.isWelcomeVisible}
          pulse={props.pulsateIconId === interfaceIdentifiers.SETTINGS}
          onClick={props.onSettingsClick}
        />
        {wrapEditingComponent(<ToolbarSpacer />)}
        {wrapEditingComponent(
          <ToolbarIconButton
            icon={props.tutorialIsActive ? "toolbarTutorialActive" : "toolbarTutorial"}
            active={props.rightPanelMode === TUTORIAL_PANEL}
            disabled={props.isWelcomeVisible}
            pulse={props.pulsateIconId === interfaceIdentifiers.TUTORIALS}
            onClick={props.onTutorialClick}
            tooltip={props.isWelcomeVisible ? null : "Toggle Tutorial Panel"}
          />
        )}
        {wrapEditingComponent(
          <ToolbarIconButton
            icon="toolbarInspector"
            active={props.rightPanelMode !== COLLAPSED_INSPECTOR_PANEL && props.rightPanelMode !== TUTORIAL_PANEL}
            disabled={props.isWelcomeVisible}
            onClick={props.onInspectorClick}
            tooltip={props.isWelcomeVisible ? null : "Toggle Inspector Panel"}
          />
        )}
      </ToolbarGroup>
    </ToolbarView>
  );
};

Toolbar.propTypes = {
  domId: PropTypes.string,
  isWelcomeVisible: PropTypes.bool.isRequired,
  includeNarrative: PropTypes.bool.isRequired,
  hasNarrativeItem: PropTypes.bool.isRequired,
  activeViewType: PropTypes.string.isRequired,
  activeViewMindmapId: PropTypes.string,
  leftPanelMode: PropTypes.string,
  rightPanelMode: PropTypes.string,
  tutorialIsActive: PropTypes.bool,
  suggestionCount: PropTypes.number,
  isTrialStarted: PropTypes.bool,
  updatesHaveExpired: PropTypes.bool,
  autoUpdateAvailable: PropTypes.bool.isRequired,
  onTimelineViewClick: PropTypes.func.isRequired,
  onRelationshipViewClick: PropTypes.func.isRequired,
  onNarrativeViewClick: PropTypes.func.isRequired,
  onMindmapViewClick: PropTypes.func.isRequired,
  onSubwayViewClick: PropTypes.func.isRequired,
  onOutlineViewClick: PropTypes.func.isRequired,
  onSpreadsheetViewClick: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func.isRequired,
  onTutorialClick: PropTypes.func.isRequired,
  onInspectorClick: PropTypes.func.isRequired,
  existingMindmaps: PropTypeShapes.mindmapList.isRequired,
  pulsateIconId: PropTypes.string,
  autoUpdatesData: PropTypes.object,
  onUpdateClick: PropTypes.func,
  showUpdateCheck: PropTypes.func,
  onNewClick: PropTypes.func,

  globalFontSize: PropTypes.number,
  availableReleaseNotes: PropTypes.string,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired

  // For readonly
};

const mapStateToProps = state => {
  return {
    isWelcomeVisible: state.present.welcome.isVisible,
    includeNarrative: definitionSelectors.isNarrativeAllowed(state.present),
    activeViewType: appStateSelectors.getActiveViewType(state.present),
    activeViewMindmapId: appStateSelectors.getActiveViewMindmapId(state.present),
    leftPanelMode: appStateSelectors.getLeftPanelMode(state.present),
    rightPanelMode: appStateSelectors.getRightPanelMode(state.present),
    //#!if readonly !== true
// tutorialIsActive: tutorialDisplaySelectors.tutorialIsActive(state.present),
    //#!endif
    autoUpdateAvailable: autoUpdatesSelectors.isUpdateAvailable(state.present),
    existingMindmaps: mindmapSelectors.getMindmapList(state.present),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present),
    hasNarrativeItem: narrativeSelectors.getNarrativeTree(state.present).children.size > 0,
    isTrialStarted: registrationSelectors.getIsTrialStarted(state.present),
    updatesHaveExpired: registrationSelectors.getUpdatesHaveExpired(state.present),
    autoUpdatesData: autoUpdatesSelectors.getAutoUpdatesData(state.present),
    globalFontSize: preferencesSelectors.getGlobalFontSize(state.present),
    availableReleaseNotes: autoUpdatesSelectors.getAvailableReleaseNotes(state.present)
  };
};

const readonlyActions = {
  onViewTypeClick: action => action,
  onTimelineViewClick: () => {
    return appStateOperations.switchViewType(TIMELINE_VIEW, null);
  },
  onRelationshipViewClick: () => {
    return appStateOperations.switchViewType(RELATIONSHIP_VIEW, null);
  },
  onNarrativeViewClick: () => {
    return appStateOperations.switchViewType(NARRATIVE_VIEW, null);
  },
  onSubwayViewClick: () => {
    return appStateOperations.switchViewType(SUBWAY_VIEW, null);
  },
  onSpreadsheetViewClick: () => {
    return appStateOperations.switchViewType(SPREADSHEET_VIEW, null);
  },
  onOutlineViewClick: () => {
    return appStateOperations.switchViewType(OUTLINE_VIEW, null);
  },
  onSettingsClick: uiStateOperations.showSettingsModal,
  onUpdateClick: autoUpdatesOperations.showUpdateModal,
  showUpdateCheck: autoUpdatesOperations.showUpdateCheck
  // onNewClick: () => {
  //   return batchOperations.batchWithAppendedActions(
  //     appStateOperations.showNotificationsInspector(Dimensions.DEFAULT_RIGHT_PANEL_WIDTH),
  //     appStateOperations.changeNotificationTab(1)
  //   );
  // }
};

const editingActions = {
  onMindmapViewClick: mindmapId => {
    if (mindmapId === CREATE_MINDMAP_ID) {
      return mindmapOperations.createMindmap();
    } else {
      return appStateOperations.switchViewType(MINDMAP_VIEW, mindmapId);
    }
  },
  onTutorialClick: appStateOperations.toggleTutorial,
  onInspectorClick: appStateOperations.toggleInspectorPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(Toolbar));
